import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth, API } from '../App';
import { t } from '../i18n';
import { toast } from 'sonner';
import { Check, X, Edit2, TrendingUp } from 'lucide-react';

export default function FAQQueuePage() {
  const { lang } = useAuth();
  const [pending, setPending] = useState([]);
  const [approved, setApproved] = useState([]);
  const [tab, setTab] = useState('pending');
  const [editId, setEditId] = useState(null);
  const [editText, setEditText] = useState('');

  const load = async () => {
    const [p, a] = await Promise.all([
      axios.get(`${API}/faqs/pending`),
      axios.get(`${API}/faqs/approved`),
    ]);
    setPending(p.data); setApproved(a.data);
  };

  useEffect(() => { load(); }, []);

  const decide = async (id, decision, edited) => {
    try {
      await axios.post(`${API}/faqs/${id}/decision`, {
        decision, edited_answer: edited || null,
      });
      toast.success(decision === 'approve' ? `Approved · future identical questions cost ₹0` : 'Rejected');
      setEditId(null); setEditText('');
      load();
    } catch (e) {
      toast.error('Action failed');
    }
  };

  const totalSavings = pending.reduce((s, f) => s + (f.estimated_monthly_savings_inr || 0), 0);

  return (
    <div className="fade-up" data-testid="faq-queue-page">
      <div className="data-label accent-line text-[#DDA079] mb-3">{t(lang, 'nav.dept_faqs')}</div>
      <h1 className="font-headings text-4xl text-white mb-2">FAQ Approval Queue</h1>
      <p className="text-slate-400 mb-8 max-w-2xl">
        When the same question keeps being routed to paid AI, the system promotes it here. Approve once → every future identical question is answered <span className="text-emerald-400 font-mono">FREE</span>.
      </p>

      <div className="grid grid-cols-3 gap-4 mb-8">
        <Stat label="Pending approval" value={pending.length} accent="text-[#DDA079]" />
        <Stat label="Approved (live)" value={approved.length} accent="text-emerald-400" />
        <Stat label="Est. monthly saving on approval" value={`₹${totalSavings.toFixed(2)}`} accent="text-white" mono />
      </div>

      <div className="flex gap-2 mb-6 border-b border-white/10">
        <TabBtn active={tab === 'pending'} onClick={() => setTab('pending')} testid="tab-pending">
          Pending · {pending.length}
        </TabBtn>
        <TabBtn active={tab === 'approved'} onClick={() => setTab('approved')} testid="tab-approved">
          Approved · {approved.length}
        </TabBtn>
      </div>

      {(tab === 'pending' ? pending : approved).length === 0 ? (
        <div className="text-slate-500 text-[14px]">{t(lang, 'common.empty')}</div>
      ) : (
        <div className="space-y-3">
          {(tab === 'pending' ? pending : approved).map(f => (
            <div key={f.id} className="v-card p-5" data-testid={`faq-${f.id}`}>
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="v-badge cost">
                      {f.source === 'doc_intelligence' ? 'AI-suggested from document' : `Asked ${f.asks_count}× this month`}
                    </span>
                    {tab === 'pending' && f.source !== 'doc_intelligence' && (
                      <span className="v-badge free flex items-center gap-1">
                        <TrendingUp size={10} /> Save ₹{f.estimated_monthly_savings_inr.toFixed(2)}/month
                      </span>
                    )}
                    {tab === 'pending' && f.source === 'doc_intelligence' && (
                      <span className="v-badge free flex items-center gap-1">
                        <TrendingUp size={10} /> ₹0 per ask once approved
                      </span>
                    )}
                    {tab === 'approved' && f.decided_by && (
                      <span className="v-badge cost">by {f.decided_by}</span>
                    )}
                  </div>
                  <div className="text-white text-[15px] mb-3">{f.question}</div>
                  {editId === f.id ? (
                    <textarea
                      data-testid={`faq-edit-${f.id}`}
                      value={editText}
                      onChange={e => setEditText(e.target.value)}
                      className="v-input text-[13px] leading-relaxed min-h-[100px] font-sans"
                    />
                  ) : (
                    <div className="text-slate-300 text-[13px] leading-relaxed whitespace-pre-wrap">
                      {f.suggested_answer}
                    </div>
                  )}
                  <div className="mt-3 text-[11px] font-mono text-slate-500">
                    Source: <span className="text-[#DDA079]">{f.citation.doc_name}</span> · p.{f.citation.page} · § {f.citation.section}
                  </div>
                </div>
              </div>

              {tab === 'pending' && (
                <div className="flex items-center gap-2 pt-3 border-t border-white/5">
                  {editId === f.id ? (
                    <>
                      <button
                        data-testid={`approve-edited-${f.id}`}
                        onClick={() => decide(f.id, 'approve', editText)}
                        className="btn-primary flex items-center gap-1.5"
                      >
                        <Check size={13} /> {t(lang, 'common.saveChanges')} & {t(lang, 'common.approve')}
                      </button>
                      <button onClick={() => { setEditId(null); setEditText(''); }} className="btn-ghost">
                        {t(lang, 'common.cancel')}
                      </button>
                    </>
                  ) : (
                    <>
                      <button
                        data-testid={`approve-${f.id}`}
                        onClick={() => decide(f.id, 'approve')}
                        className="btn-primary flex items-center gap-1.5"
                      >
                        <Check size={13} /> {t(lang, 'common.approve')} · Save ₹{f.estimated_monthly_savings_inr.toFixed(2)}/mo
                      </button>
                      <button
                        data-testid={`edit-${f.id}`}
                        onClick={() => { setEditId(f.id); setEditText(f.suggested_answer); }}
                        className="btn-secondary flex items-center gap-1.5"
                      >
                        <Edit2 size={13} /> {t(lang, 'common.edit')}
                      </button>
                      <button
                        data-testid={`reject-${f.id}`}
                        onClick={() => decide(f.id, 'reject')}
                        className="btn-danger flex items-center gap-1.5"
                      >
                        <X size={13} /> {t(lang, 'common.reject')}
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent = 'text-white', mono = false }) {
  return (
    <div className="v-card p-4">
      <div className="data-label">{label}</div>
      <div className={`mt-2 text-2xl ${accent} ${mono ? 'font-mono' : 'font-headings'}`}>{value}</div>
    </div>
  );
}

function TabBtn({ children, active, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`px-4 py-2 text-[13px] border-b-2 transition-colors ${
        active ? 'border-[#C17F59] text-white' : 'border-transparent text-slate-400 hover:text-white'
      }`}
    >
      {children}
    </button>
  );
}
